import { useCallback, useEffect, useRef, useState } from "react";
import type { ConnectionState, StreamStats } from "../types";

import { runtimeConfig } from "../runtimeConfig";

const API_BASE = runtimeConfig.apiBaseUrl;
const WS_BASE = runtimeConfig.wsBaseUrl;

import { createLogger } from "../lib/logger";
import { authService } from "../services/authService";
const slog = createLogger("webrtc");
const memlog = createLogger("memory");

const STATS_INTERVAL_MS = 2000;
const MONITOR_INTERVAL_MS = 60_000;

// ── Reconnexion bornée de la signalisation (tâche 201, P1) ──────────────────
//
// La fermeture de `18:29:23` n'a produit ni explication ni reprise : l'état
// passait à `disconnected`, le flux restait non nul et personne ne reconnectait.
// La chaîne est désormais fermée : trois tentatives, backoff avec jitter,
// plafond total de 20 s handshakes inclus, une seule chaîne à la fois.
/** Retries autorisés PAR ÉPISODE de panne — la connexion initiale n'en est pas un. */
export const CONNECT_MAX_ATTEMPTS = 3;
export const CONNECT_BACKOFF_MS = [500, 1_000, 2_000] as const;
/**
 * Budget d'un épisode de panne, handshakes inclus.
 *
 * CYCLE 2 — il courait depuis le MONTAGE de la session : après 91,8 s de
 * session saine, une fermeture `1006` était abandonnée sans une seule
 * tentative, le plafond de 20 s étant déjà consommé par l'âge de la session.
 * L'origine temporelle est désormais posée au premier échec de CHAQUE épisode.
 */
export const CONNECT_CHAIN_BUDGET_MS = 20_000;
/**
 * « VM not connected for signaling » : le backend garde alors la socket sans
 * jamais la raccorder à une VM qui revient (redémarrage, reprise, coupure de
 * son agent). Nouvel essai lent, hors budget des retries rapides.
 */
export const VM_WAIT_RETRY_MS = 10_000;
export const VM_WAIT_BUDGET_MS = 10 * 60_000;
export const VM_NOT_CONNECTED = "VM not connected for signaling";
/**
 * Durée d'ouverture au-delà de laquelle la connexion est jugée stable : le
 * compteur de retries et l'origine du budget sont alors remis à zéro, de sorte
 * qu'une panne survenant une heure plus tard reparte à 500 ms.
 */
export const STABLE_OPEN_MS = 5_000;
const RECONNECT_JITTER_RATIO = 0.25;

// ── v1.9.5 (B1) : la vidéo revient seule après une coupure réseau ──────────
//
// session A, 14/10 17:42 → 19:08 et 15/10 06:19 → 06:55 : la connexion vidéo
// était en échec mais seule la fermeture de la signalisation relançait tout,
// et la page ne l'a vue que 40 à 75 min plus tard (socket morte côté serveur).
/** Une connexion vidéo `disconnected` au-delà de ce délai est reprise ; `failed` aussitôt. */
export const PC_DISCONNECTED_GRACE_MS = 10_000;
/** Contrôle de vie applicatif : le navigateur ne voit pas les pings WebSocket du serveur. */
export const HEARTBEAT_INTERVAL_MS = 15_000;
export const HEARTBEAT_TIMEOUT_MS = 40_000;
/** Après les retries rapides, un essai lent tant que la page est ouverte. */
export const SLOW_RETRY_MS = 30_000;
const PING_MESSAGE = JSON.stringify({ type: "ping" });

/**
 * Le ping en attente depuis `pendingSince` est-il resté trop longtemps sans
 * réponse ? Seul un ping sans réponse compte : une page masquée ne vérifie
 * qu'une fois par minute, et son dernier message reçu date alors de 60 s.
 */
export function heartbeatExpired(pendingSince: number | null, now: number): boolean {
  return pendingSince !== null && now - pendingSince > HEARTBEAT_TIMEOUT_MS;
}

/** États de session qui interdisent toute reconnexion (fermeture volontaire). */
const TERMINAL_SESSION_STATES: ReadonlySet<string> = new Set([
  "stopping",
  "off",
  "destroying",
]);

/**
 * États fermés publiés par le hook — le hook de capture s'appuie dessus au lieu
 * de deviner l'éligibilité depuis un `MediaStream` résiduel.
 */
export type SignalingState =
  | "idle"
  | "connecting"
  | "open"
  | "reconnecting"
  | "closed"
  | "failed";

/** Délai avant la tentative `attempt` (1-indexée), jitter inclus. Fonction pure. */
export function reconnectDelayMs(attempt: number, random: number = Math.random()): number {
  const index = Math.min(Math.max(attempt, 1), CONNECT_BACKOFF_MS.length) - 1;
  const base = CONNECT_BACKOFF_MS[index]!;
  const bounded = Math.min(Math.max(random, 0), 1);
  return base + Math.round(base * RECONNECT_JITTER_RATIO * bounded);
}

/**
 * Décide si un nouveau RETRY est permis dans l'épisode de panne courant.
 *
 * Fonction pure : c'est elle qui porte la borne « 3 retries / 20 s ». Les deux
 * grandeurs sont relatives à l'ÉPISODE — `retriesUsed` ne compte pas la
 * connexion initiale et `episodeElapsedMs` part du premier échec inattendu, pas
 * du montage de la session.
 */
export function shouldReconnect(input: {
  retriesUsed: number;
  episodeElapsedMs: number;
  delayMs: number;
  deliberate: boolean;
  sessionState?: string | null;
}): boolean {
  if (input.deliberate) return false;
  if (TERMINAL_SESSION_STATES.has(input.sessionState ?? "")) return false;
  if (input.retriesUsed >= CONNECT_MAX_ATTEMPTS) return false;
  return input.episodeElapsedMs + input.delayMs < CONNECT_CHAIN_BUDGET_MS;
}

/** Identifiant de chaîne NON SECRET, corrélé frontend ↔ backend ↔ VM. */
export function newConnectionId(): string {
  const random = Math.random().toString(36).slice(2, 10);
  return `c-${Date.now().toString(36)}-${random}`;
}

interface UseWebRTCOptions {
  sessionId: string;
  sessionToken: string;
  /** Called when the backend sends a signaling-level message (e.g. resolution_changed). */
  onBackendMessage?: (msg: Record<string, unknown>) => void;
  /** État métier de la session : `stopping|off|destroying` interdit le reconnect. */
  sessionState?: string | null;
}

interface UseWebRTCResult {
  stream: MediaStream | null;
  connectionState: ConnectionState;
  stats: StreamStats;
  disconnect: () => void;
  sendMessage: (msg: string) => void;
  /** Send via unreliable DataChannel (low-latency, for input events). */
  sendInput: (msg: string) => void;
  /** Whether the app (not just the VM) has signaled readiness. */
  appReady: boolean;
  /**
   * v1.9.3 : le backend garde la page raccordée tant que la VM est absente
   * (redémarrage, reprise) et l'annonce par `vm_waiting` ; vrai jusqu'à
   * l'offre de la VM revenue.
   */
  vmWaiting: boolean;
  /** État fermé de la signalisation (tâche 201). */
  signalingState: SignalingState;
  /** Identifiant de la chaîne de connexion courante, jamais secret. */
  connectionId: string | null;
  /** Tentatives consommées par la chaîne courante. */
  reconnectAttempt: number;
}

export function useWebRTC({
  sessionId,
  sessionToken,
  onBackendMessage,
  sessionState = null,
}: UseWebRTCOptions): UseWebRTCResult {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("disconnected");
  const [stats, setStats] = useState<StreamStats>({
    fps: null,
    bitrate_kbps: null,
    latency_ms: null,
    resolution: null,
  });
  const [appReady, setAppReady] = useState(false);
  const [vmWaiting, setVmWaiting] = useState(false);
  const [signalingState, setSignalingState] = useState<SignalingState>("idle");
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  const onBackendMsgRef = useRef(onBackendMessage);
  onBackendMsgRef.current = onBackendMessage;
  const sessionStateRef = useRef(sessionState);
  sessionStateRef.current = sessionState;
  /** Génération de tentative : une callback d'ancienne génération est inerte. */
  const attemptGenerationRef = useRef(0);
  /** Fermeture volontaire/terminale : plus aucune reconnexion. */
  const deliberateRef = useRef(false);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const inputDcRef = useRef<RTCDataChannel | null>(null);
  const inputQueueRef = useRef<string[]>([]);
  const statsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const monitorTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevBytesRef = useRef(0);
  const prevTimeRef = useRef(0);
  const prevUsedMBRef = useRef(0);
  const sessionStartRef = useRef(0);
  const cleanedUpRef = useRef(false);

  const cleanup = useCallback(() => {
    if (cleanedUpRef.current) return;
    cleanedUpRef.current = true;

    const durationMin = sessionStartRef.current
      ? Math.round((Date.now() - sessionStartRef.current) / 60_000)
      : 0;
    slog.info("cleanup", { durationMin });

    if (statsTimerRef.current) {
      clearInterval(statsTimerRef.current);
      statsTimerRef.current = null;
    }
    if (monitorTimerRef.current) {
      clearInterval(monitorTimerRef.current);
      monitorTimerRef.current = null;
    }
    if (inputDcRef.current) {
      inputDcRef.current.close();
      inputDcRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    setStream(null);
    setConnectionState("disconnected");
    setAppReady(false);
    setVmWaiting(false);
    // Un nettoyage complet est toujours terminal : aucune reconnexion ne doit
    // survivre à un unmount, un changement de session ou un `type=close`.
    deliberateRef.current = true;
    attemptGenerationRef.current += 1;
    setSignalingState("closed");
  }, []);

  useEffect(() => {
    if (!sessionId || !sessionToken) return;
    cleanedUpRef.current = false;
    deliberateRef.current = false;

    let cancelled = false;
    // Une seule chaîne de connexion à la fois, identifiée et bornée.
    const chainId = newConnectionId();
    // ── État d'ÉPISODE de panne (cycle 2) ───────────────────────────────────
    // `retriesUsed` ne compte que les retries, jamais la connexion initiale.
    // `episodeStartedAt` est posé au premier échec inattendu et remis à `null`
    // dès qu'une ouverture est jugée stable : chaque panne a son propre budget.
    let retriesUsed = 0;
    let episodeStartedAt: number | null = null;
    let stableTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    /** Début de l'attente d'une VM absente ; remis à null dès une offre SDP. */
    let vmWaitStartedAt: number | null = null;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    /** Premier ping resté sans réponse ; remis à null dès qu'un message arrive. */
    let pendingPingSince: number | null = null;
    /** Armé au premier `pong` : un serveur qui ne répond pas ne coupe rien. */
    let heartbeatArmed = false;
    let pcDisconnectedTimer: ReturnType<typeof setTimeout> | null = null;
    /** Vrai pendant l'attente d'un essai lent, après les retries rapides. */
    let slowRetryPending = false;
    setConnectionId(chainId);
    setReconnectAttempt(0);

    function clearStableTimer() {
      if (stableTimer) {
        clearTimeout(stableTimer);
        stableTimer = null;
      }
    }

    function clearHeartbeat() {
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
      pendingPingSince = null;
      heartbeatArmed = false;
    }

    function clearPcDisconnectedTimer() {
      if (pcDisconnectedTimer) {
        clearTimeout(pcDisconnectedTimer);
        pcDisconnectedTimer = null;
      }
    }

    /**
     * Ping applicatif toutes les 15 s. Un ping resté plus de 40 s sans aucune
     * réponse du serveur ferme cette tentative et relance tout.
     */
    function startHeartbeat(ws: WebSocket) {
      clearHeartbeat();
      heartbeatTimer = setInterval(() => {
        if (cancelled || cleanedUpRef.current || deliberateRef.current) {
          clearHeartbeat();
          return;
        }
        if (ws.readyState !== WebSocket.OPEN) return;
        const now = Date.now();
        if (heartbeatArmed && heartbeatExpired(pendingPingSince, now)) {
          const silentMs = now - (pendingPingSince ?? now);
          slog.warn("signaling heartbeat lost", { connectionId: chainId, silentMs });
          scheduleReconnect("heartbeat_timeout", { silentMs });
          return;
        }
        try {
          ws.send(PING_MESSAGE);
        } catch {
          // Socket en cours de fermeture : son `onclose` relancera.
        }
        if (pendingPingSince === null) pendingPingSince = now;
      }, HEARTBEAT_INTERVAL_MS);
    }

    /** La connexion vidéo est perdue : tout reprendre, signalisation comprise. */
    function recoverMedia(pc: RTCPeerConnection, cause: string) {
      slog.warn("video transport lost, reconnecting", {
        connectionId: chainId,
        cause,
        peerConnectionState: pc.connectionState,
        iceConnectionState: pc.iceConnectionState,
        signalingOpen: wsRef.current?.readyState === WebSocket.OPEN,
      });
      scheduleReconnect(cause, { peerConnectionState: pc.connectionState });
    }

    /**
     * Les retries rapides sont épuisés mais la coupure peut durer : un nouvel
     * essai toutes les 30 s tant que la page est ouverte et la session vivante,
     * et tout de suite au retour du réseau ou de l'onglet.
     */
    function scheduleSlowRetry(cause: string) {
      teardownAttempt();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      slowRetryPending = true;
      slog.info("signaling slow retry scheduled", {
        connectionId: chainId,
        cause,
        delayMs: SLOW_RETRY_MS,
      });
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        runSlowRetry("timer");
      }, SLOW_RETRY_MS);
    }

    function runSlowRetry(trigger: string) {
      if (!slowRetryPending) return;
      if (cancelled || cleanedUpRef.current || deliberateRef.current) return;
      if (TERMINAL_SESSION_STATES.has(sessionStateRef.current ?? "")) return;
      slowRetryPending = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      slog.info("signaling slow retry", { connectionId: chainId, trigger });
      void connect({ slow: true });
    }

    /** Clôt l'épisode courant : la prochaine panne repartira de 500 ms. */
    function closeEpisode(reason: string) {
      if (retriesUsed === 0 && episodeStartedAt === null) return;
      slog.info("signaling episode cleared", {
        connectionId: chainId,
        reason,
        retriesUsed,
      });
      retriesUsed = 0;
      episodeStartedAt = null;
      setReconnectAttempt(0);
    }

    /** Ferme la tentative précédente sans toucher à l'état de la chaîne. */
    function teardownAttempt() {
      setVmWaiting(false);
      clearStableTimer();
      clearHeartbeat();
      clearPcDisconnectedTimer();
      if (statsTimerRef.current) {
        clearInterval(statsTimerRef.current);
        statsTimerRef.current = null;
      }
      if (monitorTimerRef.current) {
        clearInterval(monitorTimerRef.current);
        monitorTimerRef.current = null;
      }
      if (inputDcRef.current) {
        inputDcRef.current.onopen = null;
        inputDcRef.current.onclose = null;
        inputDcRef.current.onerror = null;
        inputDcRef.current.close();
        inputDcRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      if (pcRef.current) {
        pcRef.current.onicecandidate = null;
        pcRef.current.ontrack = null;
        pcRef.current.ondatachannel = null;
        pcRef.current.onconnectionstatechange = null;
        pcRef.current.oniceconnectionstatechange = null;
        pcRef.current.onicegatheringstatechange = null;
        pcRef.current.close();
        pcRef.current = null;
      }
    }

    /**
     * Planifie une reconnexion si — et seulement si — la fermeture était
     * inattendue, la session encore capturable et la chaîne dans ses bornes.
     */
    function scheduleReconnect(cause: string, detail: Record<string, unknown> = {}) {
      if (cancelled || cleanedUpRef.current || deliberateRef.current) return;
      // Le budget démarre ICI, au premier échec de l'épisode — jamais au
      // montage. Une session saine pendant une heure conserve donc ses 20 s.
      if (episodeStartedAt === null) {
        episodeStartedAt = Date.now();
      }
      const delay = reconnectDelayMs(retriesUsed + 1);
      const episodeElapsedMs = Date.now() - episodeStartedAt;
      if (
        !shouldReconnect({
          retriesUsed,
          episodeElapsedMs,
          delayMs: delay,
          deliberate: deliberateRef.current,
          sessionState: sessionStateRef.current,
        })
      ) {
        slog.warn("signaling reconnect abandoned", {
          connectionId: chainId,
          cause,
          retriesUsed,
          episodeElapsedMs,
          sessionState: sessionStateRef.current,
          ...detail,
        });
        setSignalingState("failed");
        // v1.9.5 (B1) : seule une session qui s'arrête ferme définitivement la
        // chaîne ; une coupure réseau plus longue que 20 s passe en essais lents.
        if (!TERMINAL_SESSION_STATES.has(sessionStateRef.current ?? "")) {
          scheduleSlowRetry(cause);
        }
        return;
      }
      setSignalingState("reconnecting");
      slog.info("signaling reconnect scheduled", {
        connectionId: chainId,
        cause,
        retry: retriesUsed + 1,
        delayMs: delay,
        episodeElapsedMs,
        ...detail,
      });
      teardownAttempt();
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect({ retry: true });
      }, delay);
    }

    /**
     * La VM n'est pas (encore) joignable : fermer cette tentative, que le
     * backend garderait ouverte sans fin, et réessayer toutes les 10 s pendant
     * 10 min. N'entame pas le budget des retries rapides.
     */
    function waitForVm() {
      if (cancelled || cleanedUpRef.current || deliberateRef.current) return;
      if (TERMINAL_SESSION_STATES.has(sessionStateRef.current ?? "")) return;
      if (vmWaitStartedAt === null) vmWaitStartedAt = Date.now();
      const waitedMs = Date.now() - vmWaitStartedAt;
      if (waitedMs + VM_WAIT_RETRY_MS > VM_WAIT_BUDGET_MS) {
        slog.warn("vm wait abandoned", { connectionId: chainId, waitedMs });
        setSignalingState("failed");
        return;
      }
      slog.info("vm not connected, retrying", {
        connectionId: chainId,
        waitedMs,
        delayMs: VM_WAIT_RETRY_MS,
      });
      teardownAttempt();
      setSignalingState("reconnecting");
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect();
      }, VM_WAIT_RETRY_MS);
    }

    async function connect({
      retry = false,
      slow = false,
    }: { retry?: boolean; slow?: boolean } = {}) {
      if (cancelled || cleanedUpRef.current || deliberateRef.current) return;
      const generation = ++attemptGenerationRef.current;
      /** Vrai tant que cette tentative est la tentative courante. */
      const current = () =>
        !cancelled &&
        !cleanedUpRef.current &&
        generation === attemptGenerationRef.current;
      // La connexion initiale n'est pas un retry : elle ne consomme ni budget
      // ni palier de backoff (le premier retry doit partir à 500 ms). Un essai
      // lent (v1.9.5) n'en consomme pas non plus.
      if (retry) {
        retriesUsed += 1;
        setReconnectAttempt(retriesUsed);
      }
      setSignalingState(retry || slow ? "reconnecting" : "connecting");
      setConnectionState("connecting");
      sessionStartRef.current = Date.now();
      prevUsedMBRef.current = 0;
      slog.info("connecting", {
        sessionId,
        connectionId: chainId,
        retry,
        slow,
        retriesUsed,
      });

      // 1. Fetch ICE servers (TURN credentials) — best effort
      //    Skip external STUN on localhost/LAN — saves ~5s ICE gathering time
      const isLocal =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1" ||
        window.location.hostname.startsWith("192.168.") ||
        window.location.hostname.startsWith("10.");
      let iceServers: RTCIceServer[] = isLocal
        ? []
        : [{ urls: "stun:stun.l.google.com:19302" }];
      try {
        // TURN route is behind Keycloak JWT auth, not the HMAC session token
        const jwt = authService.getToken();
        const res = await fetch(
          `${API_BASE}/turn/credentials?session_id=${sessionId}`,
          { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} },
        );
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.iceServers)) {
            iceServers = data.iceServers;
          }
        }
      } catch {
        // Use default (empty for local, STUN for remote)
      }

      if (cancelled) return;

      // 2. Create RTCPeerConnection
      const pc = new RTCPeerConnection({
        iceServers,
        iceCandidatePoolSize: 1,
      });
      pcRef.current = pc;

      // Pre-create receive-only transceivers so the browser is ready
      // to accept both video and audio from the VM's first SDP offer.
      pc.addTransceiver("video", { direction: "recvonly" });
      pc.addTransceiver("audio", { direction: "recvonly" });

      // Collect incoming tracks (video + audio) into one MediaStream.
      // On re-negotiation, replace same-kind tracks to avoid stale "ended" tracks.
      const combinedStream = new MediaStream();
      pc.ontrack = (ev) => {
        const t = ev.track;
        const tid = `${t.kind}/${t.id.slice(0, 8)}`;
        slog.info(`track ${tid}: ontrack`, {
          muted: t.muted,
          readyState: t.readyState,
          enabled: t.enabled,
          streams: ev.streams.length,
        });
        t.addEventListener("mute", () => slog.info(`track ${tid}: mute`));
        t.addEventListener("unmute", () => slog.info(`track ${tid}: unmute`));
        t.addEventListener("ended", () => slog.warn(`track ${tid}: ended`));

        // Remove any existing track of the same kind (video/audio)
        for (const old of combinedStream.getTracks()) {
          if (old.kind === ev.track.kind) {
            combinedStream.removeTrack(old);
          }
        }
        combinedStream.addTrack(ev.track);
        setStream(new MediaStream(combinedStream.getTracks()));
        // ontrack only means SDP announced a track. ICE may still fail;
        // only the actual PeerConnection can declare the transport connected.
      };

      // Monitor connection state. v1.9.5 (B1) : `failed`, ou `disconnected`
      // au-delà de 10 s, relance tout ; avant, rien ne se passait.
      pc.onconnectionstatechange = () => {
        if (!current()) return;
        const uptimeMin = Math.round((Date.now() - sessionStartRef.current) / 60_000);
        slog.info("connectionState", { state: pc.connectionState, uptimeMin });
        switch (pc.connectionState) {
          case "connected":
            clearPcDisconnectedTimer();
            setConnectionState("connected");
            break;
          case "disconnected":
            setConnectionState("disconnected");
            if (!pcDisconnectedTimer) {
              pcDisconnectedTimer = setTimeout(() => {
                pcDisconnectedTimer = null;
                if (current() && pc.connectionState === "disconnected") {
                  recoverMedia(pc, "pc_disconnected");
                }
              }, PC_DISCONNECTED_GRACE_MS);
            }
            break;
          case "failed":
            clearPcDisconnectedTimer();
            setConnectionState("disconnected");
            recoverMedia(pc, "pc_failed");
            break;
          case "closed":
            clearPcDisconnectedTimer();
            setConnectionState("disconnected");
            break;
        }
      };

      // Monitor ICE connection state (more granular than connectionState)
      pc.oniceconnectionstatechange = () => {
        slog.info("iceConnectionState", { state: pc.iceConnectionState });
      };

      pc.onicegatheringstatechange = () => {
        slog.debug("iceGatheringState", { state: pc.iceGatheringState });
      };

      // 2b. DataChannel for input (keyboard/mouse)
      // The VM creates the DC named "inputs" (included in its SDP offer).
      // We receive it here and also create our own as fallback.
      const setupDc = (dc: RTCDataChannel) => {
        inputDcRef.current = dc;
        dc.onopen = () => {
          slog.info("inputDC open", { label: dc.label });
          const queue = inputQueueRef.current;
          inputQueueRef.current = [];
          for (const msg of queue) {
            dc.send(msg);
          }
        };
        dc.onclose = () => slog.info("inputDC closed");
        dc.onerror = (ev) => slog.warn("inputDC error", { error: (ev as ErrorEvent).message ?? "unknown" });
      };

      // Receive VM-created DataChannel (log only — we use our own for sending)
      pc.ondatachannel = (ev) => {
        slog.info("ondatachannel", { label: ev.channel.label });
      };

      // Create our own DataChannel for input (works if VM's offer includes SCTP)
      // Browser sends on this DC → VM receives via _on_data_channel handler
      try {
        // A lost or reordered keyup leaves Ctrl/Alt held in the guest. Input
        // transitions require ordered delivery and retransmission; mouse moves
        // are already throttled by the capture hook.
        const localDc = pc.createDataChannel("inputs", { ordered: true });
        setupDc(localDc);
      } catch { /* ignore if SCTP not negotiated yet */ }

      // 3. Connect to signaling WebSocket
      // `connection_id` est un identifiant de corrélation NON SECRET : il est
      // ajouté à la query, jamais concaténé au jeton, et jamais journalisé
      // avec l'URL (les journaux ne portent que l'identifiant lui-même).
      const wsUrl =
        `${WS_BASE}/sessions/${sessionId}/signaling` +
        `?token=${sessionToken}&connection_id=${encodeURIComponent(chainId)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Send ICE candidates to VM via signaling
      pc.onicecandidate = (ev) => {
        if (ev.candidate && ws.readyState === WebSocket.OPEN) {
          ws.send(
            JSON.stringify({
              type: "ice",
              candidate: ev.candidate.candidate,
              sdpMLineIndex: ev.candidate.sdpMLineIndex,
              sdpMid: ev.candidate.sdpMid,
            }),
          );
        }
      };

      ws.onmessage = async (ev) => {
        // Tout message prouve que la signalisation vit : le ping en attente
        // a sa réponse.
        pendingPingSince = null;
        let msg: {
          type: string;
          sdp?: string;
          candidate?: string;
          sdpMLineIndex?: number;
          sdpMid?: string;
          message?: string;
          reason?: string;
        };
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }

        if (msg.type === "pong") {
          // Réponse au contrôle de vie : jamais transmise à l'appelant.
          heartbeatArmed = true;
          return;
        }
        if (msg.type === "offer" && msg.sdp) {
          vmWaitStartedAt = null;
          setVmWaiting(false);
          // Set remote description (VM's offer)
          await pc.setRemoteDescription(
            new RTCSessionDescription({ type: "offer", sdp: msg.sdp }),
          );
          // Create answer
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          // Send answer back
          ws.send(
            JSON.stringify({
              type: "answer",
              sdp: answer.sdp,
            }),
          );
        } else if (msg.type === "ice" && msg.candidate) {
          try {
            await pc.addIceCandidate(
              new RTCIceCandidate({
                candidate: msg.candidate,
                sdpMLineIndex: msg.sdpMLineIndex ?? 0,
                sdpMid: msg.sdpMid,
              }),
            );
          } catch {
            // ICE candidate may arrive before remote description is set
          }
        } else if (msg.type === "error") {
          slog.error("Signaling error", { message: msg.message });
          if (msg.message === VM_NOT_CONNECTED) waitForVm();
        } else if (msg.type === "close") {
          // Fermeture volontaire annoncée par le backend : terminal, zéro
          // reconnexion (fiche 201).
          slog.info("Session closed", {
            reason: msg.reason,
            connectionId: chainId,
          });
          deliberateRef.current = true;
          cleanup();
        } else if (msg.type === "vm_waiting") {
          // Le backend reste raccordé et rattachera la VM à son retour :
          // pas de fermeture ni de nouvel essai ici.
          slog.info("vm not connected yet, socket kept", { connectionId: chainId });
          setVmWaiting(true);
        } else if (msg.type === "app_ready") {
          setAppReady(true);
        } else {
          // Forward non-signaling messages (e.g. resolution_changed) to caller
          onBackendMsgRef.current?.(msg as Record<string, unknown>);
        }
      };

      ws.onopen = () => {
        if (!current()) return;
        slog.info("ws open", { connectionId: chainId, retriesUsed });
        setSignalingState("open");
        // Une ouverture n'est pas une reprise : elle doit TENIR. Après ce
        // délai, l'épisode est clos et une panne ultérieure — une heure plus
        // tard — repart avec un budget neuf et un premier retry à 500 ms.
        clearStableTimer();
        stableTimer = setTimeout(() => {
          stableTimer = null;
          if (!current()) return;
          closeEpisode("stable_open");
        }, STABLE_OPEN_MS);
        startHeartbeat(ws);
      };

      ws.onclose = (ev) => {
        if (!current()) return;
        const ageMs = Date.now() - sessionStartRef.current;
        // Diagnostic corrélé : code, raison, propreté, âge, états PC/ICE, canal
        // de données et dernière progression vidéo. Ni URL, ni jeton.
        slog.warn("ws close", {
          connectionId: chainId,
          retriesUsed,
          episodeElapsedMs:
            episodeStartedAt === null ? 0 : Date.now() - episodeStartedAt,
          code: ev.code,
          reason: ev.reason,
          wasClean: ev.wasClean,
          ageMs,
          uptimeMin: Math.round(ageMs / 60_000),
          peerConnectionState: pcRef.current?.connectionState ?? "none",
          iceConnectionState: pcRef.current?.iceConnectionState ?? "none",
          dataChannelState: inputDcRef.current?.readyState ?? "none",
          lastVideoBytes: prevBytesRef.current,
          lastVideoProgressAgoMs: prevTimeRef.current
            ? Date.now() - prevTimeRef.current
            : null,
          sessionState: sessionStateRef.current,
          deliberate: deliberateRef.current,
        });
        setConnectionState("disconnected");
        if (deliberateRef.current) {
          setSignalingState("closed");
          return;
        }
        scheduleReconnect("ws_close", { code: ev.code, wasClean: ev.wasClean });
      };

      ws.onerror = () => {
        if (!current()) return;
        slog.error("ws error", { connectionId: chainId, retriesUsed });
      };

      // 4. Start stats collection
      statsTimerRef.current = setInterval(async () => {
        if (!pc || pc.connectionState !== "connected") return;
        try {
          const report = await pc.getStats();
          report.forEach((s) => {
            if (s.type === "inbound-rtp" && s.kind === "video") {
              const now = Date.now();
              const bytes = s.bytesReceived ?? 0;
              if (prevTimeRef.current > 0) {
                const dt = (now - prevTimeRef.current) / 1000;
                const db = bytes - prevBytesRef.current;
                const kbps = Math.round((db * 8) / dt / 1000);
                setStats((prev) => ({
                  ...prev,
                  bitrate_kbps: kbps,
                  fps: s.framesPerSecond ?? prev.fps,
                  resolution:
                    s.frameWidth && s.frameHeight
                      ? `${s.frameWidth}x${s.frameHeight}`
                      : prev.resolution,
                }));
              }
              prevBytesRef.current = bytes;
              prevTimeRef.current = now;
            }
            if (s.type === "candidate-pair" && s.state === "succeeded") {
              setStats((prev) => ({
                ...prev,
                latency_ms:
                  s.currentRoundTripTime != null
                    ? Math.round(s.currentRoundTripTime * 1000)
                    : prev.latency_ms,
              }));
            }
          });
        } catch {
          // Stats collection can fail during teardown
        }
      }, STATS_INTERVAL_MS);

      // 5. Start 60s monitor — memory + stats snapshot pushed to logs
      monitorTimerRef.current = setInterval(() => {
        const uptimeMin = Math.round((Date.now() - sessionStartRef.current) / 60_000);

        // Memory (Chrome only)
        const mem = (performance as unknown as { memory?: {
          usedJSHeapSize: number;
          totalJSHeapSize: number;
          jsHeapSizeLimit: number;
        } }).memory;
        if (mem) {
          const usedMB = +(mem.usedJSHeapSize / 1_048_576).toFixed(1);
          const totalMB = +(mem.totalJSHeapSize / 1_048_576).toFixed(1);
          const limitMB = +(mem.jsHeapSizeLimit / 1_048_576).toFixed(0);
          const deltaMB = prevUsedMBRef.current
            ? +(usedMB - prevUsedMBRef.current).toFixed(1)
            : 0;
          prevUsedMBRef.current = usedMB;
          memlog.info("heap", { usedMB, totalMB, limitMB, deltaMB, uptimeMin });
        }

        // WebRTC stats snapshot
        if (pc && pc.connectionState === "connected") {
          pc.getStats().then((report) => {
            let fps: number | null = null;
            let bitrateKbps: number | null = null;
            let rtt: number | null = null;
            let resolution: string | null = null;
            let packetsLost = 0;
            let packetsReceived = 0;
            let jitter: number | null = null;

            report.forEach((s) => {
              if (s.type === "inbound-rtp" && s.kind === "video") {
                fps = s.framesPerSecond ?? null;
                resolution = s.frameWidth && s.frameHeight
                  ? `${s.frameWidth}x${s.frameHeight}` : null;
                packetsLost = s.packetsLost ?? 0;
                packetsReceived = s.packetsReceived ?? 0;
              }
              if (s.type === "inbound-rtp" && s.kind === "audio") {
                jitter = s.jitter ?? null;
              }
              if (s.type === "candidate-pair" && s.state === "succeeded") {
                rtt = s.currentRoundTripTime != null
                  ? Math.round(s.currentRoundTripTime * 1000) : null;
                bitrateKbps = s.availableOutgoingBitrate != null
                  ? Math.round(s.availableOutgoingBitrate / 1000) : null;
              }
            });

            const lossRate = packetsReceived > 0
              ? +((packetsLost / (packetsReceived + packetsLost)) * 100).toFixed(2)
              : 0;

            slog.info("stats", {
              uptimeMin,
              fps,
              bitrateKbps,
              rtt,
              resolution,
              packetsLost,
              lossRate,
              jitter,
            });
          }).catch(() => {});
        }
      }, MONITOR_INTERVAL_MS);
    }

    // Retour du réseau ou de l'onglet : l'essai lent en attente part aussitôt.
    const onOnline = () => runSlowRetry("online");
    const onVisible = () => {
      if (document.visibilityState === "visible") runSlowRetry("visible");
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);

    void connect({ retry: false });

    return () => {
      cancelled = true;
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
      clearStableTimer();
      clearHeartbeat();
      clearPcDisconnectedTimer();
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      cleanup();
    };
  }, [sessionId, sessionToken, cleanup]);

  const sendMessage = useCallback((msg: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(msg);
    }
  }, []);

  const sendInput = useCallback((msg: string) => {
    const dc = inputDcRef.current;
    if (dc && dc.readyState === "open") {
      dc.send(msg);
    } else if (dc && dc.readyState === "connecting") {
      // Queue while channel is opening (max 256)
      if (inputQueueRef.current.length < 256) {
        inputQueueRef.current.push(msg);
      }
    } else {
      // Fallback to signaling WebSocket if DC not available
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(msg);
      }
    }
  }, []);

  return {
    stream,
    connectionState,
    stats,
    disconnect: cleanup,
    sendMessage,
    sendInput,
    appReady,
    vmWaiting,
    signalingState,
    connectionId,
    reconnectAttempt,
  };
}
